import React from "react";
import {
    Alert,
    Button,
    Descriptions,
    Divider,
    Drawer,
    Empty,
    Space,
    Table,
    Tag,
    Typography,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import {
    formatAmount,
    formatDateTime,
    getActionColor,
    getActionLabel,
    getChildFillTypeColor,
    getFillTypeLabel,
    getParentStatusColor,
    getParentStatusLabel,
} from "./transactionScheduleUi";
import { isEmptySchedule } from "./transactionScheduleRules";
import TransactionScheduleAttachments from "./TransactionScheduleAttachments";

const { Text } = Typography;

function TransactionScheduleDetailDrawer({
    open,
    onClose,
    selectedRecord,
    detailData,
    statusLogs,
    loading,
    onManage,
    attachments = [],
    onViewGeneratedSlipPdf,
    onDownloadGeneratedSlipPdf,
    onViewGeneratedSampleFilledPdf,
    onDownloadGeneratedSampleFilledPdf,

}) {
    const isEmpty = isEmptySchedule(selectedRecord);

    const allocationColumns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 90,
        },
        {
            title: "Дүн",
            dataIndex: "allocatedAmount",
            key: "allocatedAmount",
            width: 140,
            render: (value) => formatAmount(value),
        },
        {
            title: "Төлөв",
            dataIndex: "fillType",
            key: "fillType",
            width: 130,
            render: (value) => (
                <Tag color={getChildFillTypeColor(value)}>
                    {getFillTypeLabel(value)}
                </Tag>
            ),
        },
        {
            title: "Үүсгэсэн PDF",
            key: "generatedPdfs",
            width: 280,
            render: (_, row) => {
                const hasSlip = !!row.generatedSlipPdfFileName;
                const hasSample = !!row.generatedSampleFilledPdfFileName;

                if (!hasSlip && !hasSample) {
                    return <Text type="secondary">-</Text>;
                }

                return (
                    <Space direction="vertical" size={6}>
                        {hasSlip ? (
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 600 }}>Slip PDF</div>
                                <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                                    {row.generatedSlipPdfFileName}
                                </div>
                                <Space size={6}>
                                    <Button size="small" onClick={() => onViewGeneratedSlipPdf?.(row)}>
                                        Харах
                                    </Button>
                                    <Button size="small" onClick={() => onDownloadGeneratedSlipPdf?.(row)}>
                                        Татах
                                    </Button>
                                </Space>
                            </div>
                        ) : null}

                        {hasSample ? (
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 600 }}>Sample Filled PDF</div>
                                <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                                    {row.generatedSampleFilledPdfFileName}
                                </div>
                                <Space size={6}>
                                    <Button size="small" onClick={() => onViewGeneratedSampleFilledPdf?.(row)}>
                                        Харах
                                    </Button>
                                    <Button size="small" onClick={() => onDownloadGeneratedSampleFilledPdf?.(row)}>
                                        Татах
                                    </Button>
                                </Space>
                            </div>
                        ) : null}
                    </Space>
                );
            },
        },
        // {
        //     title: "Үүсгэсэн огноо",
        //     dataIndex: "createdAt",
        //     key: "createdAt",
        //     width: 180,
        //     render: (value) => formatDateTime(value),
        // },
    ];

    const logColumns = [
        {
            title: "Огноо",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 180,
            render: (value) => formatDateTime(value),
        },
        {
            title: "Үйлдэл",
            dataIndex: "actionType",
            key: "actionType",
            width: 140,
            render: (value) => (
                <Tag color={getActionColor(value)}>
                    {getActionLabel(value)}
                </Tag>
            ),
        },
        {
            title: "Хуваарилалтын ID",
            dataIndex: "transactionScheduleAllocationId",
            key: "transactionScheduleAllocationId",
            width: 150,
        },
    ];

    return (
        <Drawer
            title="Гүйлгээний хуваарийн дэлгэрэнгүй"
            placement="right"
            width={960}
            open={open}
            onClose={onClose}
            destroyOnClose={false}
        >
            {!selectedRecord ? (
                <Empty description="Мэдээлэл сонгогдоогүй байна" />
            ) : (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 2 }}
                    >
                        <Descriptions.Item label="ID">
                            {selectedRecord.id}
                        </Descriptions.Item>
                        <Descriptions.Item label="Хүсэлтийн дугаар">
                            {selectedRecord.requestNumber || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Төлөгч">
                            {selectedRecord.payer || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Төлбөрийн дүн">
                            {formatAmount(selectedRecord.paymentAmount)} {selectedRecord.currency || ""}
                        </Descriptions.Item>
                        <Descriptions.Item label="Төлөв">
                            <Tag color={getParentStatusColor(selectedRecord.parentDisplayStatus)}>
                                {getParentStatusLabel(selectedRecord.parentDisplayStatus)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Үлдэгдэл дүн">
                            {formatAmount(selectedRecord.remainingAmount)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Хүлээн авагч байгууллага">
                            {selectedRecord.beneficiaryCompany || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Банк">
                            {selectedRecord.beneficiaryBank || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Данс">
                            {selectedRecord.beneficiaryAccount || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Огноо">
                            {formatDateTime(selectedRecord.createdAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Гүйлгээний утга" span={2}>
                            {selectedRecord.paymentPurpose || "-"}
                        </Descriptions.Item>
                    </Descriptions>

                    <TransactionScheduleAttachments attachments={attachments} />

                    {isEmpty ? (
                        <>
                            <Alert
                                type="info"
                                showIcon
                                message="Энэ хүсэлт дээр хуваарилалт үүсээгүй байна"
                                description="Үндсэн мэдээлэл болон хавсралтуудыг харах боломжтой. Хуваарилалт хийх бол доорх товчийг ашиглана."
                            />

                            <Space>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={() => onManage?.(selectedRecord)}
                                >
                                    Хуваарь удирдах
                                </Button>
                            </Space>
                        </>
                    ) : (
                        <>
                            <Space
                                align="center"
                                style={{
                                    justifyContent: "space-between",
                                    width: "100%",
                                }}
                            >
                                <Text strong>Хуваарилалтын мэдээлэл</Text>
                                <Button
                                    icon={<EditOutlined />}
                                    onClick={() => onManage?.(selectedRecord)}
                                >
                                    Хуваарь удирдах
                                </Button>
                            </Space>

                            <Table
                                rowKey={(row) => row.id}
                                loading={loading}
                                columns={allocationColumns}
                                dataSource={detailData?.allocations || []}
                                pagination={false}
                                scroll={{ x: 700 }}
                                size="small"
                            />
                        </>
                    )}

                    <Divider style={{ margin: "8px 0" }} />

                    <Text strong>Төлөв өөрчлөлтийн түүх</Text>
                    <Table
                        rowKey={(row, index) => row.id || index}
                        loading={loading}
                        columns={logColumns}
                        dataSource={statusLogs || []}
                        pagination={{ pageSize: 5 }}
                        scroll={{ x: 650 }}
                        size="small"
                    />
                </Space>
            )}
        </Drawer>
    );
}

export default TransactionScheduleDetailDrawer;